<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12 component-title-row">
          <h3>
            <i class="fa fa-fw fa-handshake"></i> {{ _('Partners') }}
          </h3>
          <div class="component-title-buttons">
            <button class="newSelector btn btn-success addbutton" type="button" v-on:click="createPartner"><i class="fa fa-fw fa-plus"></i> {{ _('Create partner') }}</button>
          </div>
        </div>
      </div>
      <div class="list-filter-row">
        <div class="list-filter-col">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Status') }}</div>
              <select class="form-control" id="partnerStatusFilter" v-model="statusFilter">
                <option value="">{{ _('All') }}</option>
                <option v-for="status in partnerStatuses" :key="status.status" :value="status.status">{{ _(status.label) }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="list-filter-col">
          <div class="checkbox">
            <label>
              <input type="checkbox" v-model="readyForReview" />
              {{ _('Pilots ready for review') }}
              <span class="badge" v-if="readyCount">{{ readyCount }}</span>
            </label>
          </div>
        </div>
      </div>
      <p class="text-muted" v-if="loaded && !filteredPartners.length">{{ partners.length ? _('No partners match the filter.') : _('No partners yet.') }}</p>
      <div class="partner-table-wrap" v-if="filteredPartners.length">
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <th>{{ _('Name') }}</th>
              <th>{{ _('Status') }}</th>
              <th class="text-right">{{ _('Integrations') }}</th>
              <th class="text-right">{{ _('Connected shops') }}</th>
              <th class="text-right">{{ _('Requests, 24 h') }}</th>
              <th class="text-right">{{ _('Error rate') }}</th>
              <th>{{ _('Last used') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="partner-row" v-for="partner in filteredPartners" :key="partner.id" v-on:click="openPartner(partner)">
              <td>
                {{ partner.name }}
                <div class="text-muted small" v-if="partner.website">{{ partner.website }}</div>
              </td>
              <td>
                <span :class="statusClass(partner.status)">{{ statusLabel(partner.status) }}</span>
                <span class="label label-warning partner-review" v-if="partner.ready_for_review">{{ _('Ready for review') }}</span>
              </td>
              <td class="text-right">{{ formatCount(partner.integrations) }}</td>
              <td class="text-right">{{ formatCount(partner.connected_shops) }}</td>
              <td class="text-right">{{ formatCount(partner.requests_24h) }}</td>
              <td class="text-right">{{ formatRate(partner.error_rate_24h) }}</td>
              <td>{{ formatApiTime(partner.last_used_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import debounce from 'lodash/debounce'
import components from '~/lib/components'
import partnerapi from '~/lib/partnerapi'

export default {
  mixins: [components, partnerapi],
  data () {
    return {
      partners: [],
      loaded: false,
      statusFilter: '',
      readyForReview: false
    }
  },
  computed: {
    readyCount () {
      return this.partners.filter(p => p.ready_for_review).length
    },
    filteredPartners () {
      return this.partners.filter(partner => {
        if (this.statusFilter && partner.status !== this.statusFilter) {
          return false
        }
        if (this.readyForReview && !partner.ready_for_review) {
          return false
        }
        return true
      })
    }
  },
  methods: {
    async loadPartners () {
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/admin/partners.json?apikey=' + this.apiKey)
        const rows = this.listData(data)
        rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        this.partners = rows
      } catch (e) {
        this.showError(e, this._('Loading the partners failed.'), true)
      } finally {
        this.loaded = true
      }
    },
    openPartner (partner) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'AdminPartnerDetails',
        title: this._('Partner') + ': ' + partner.name,
        width: 6,
        data: {
          partnerId: partner.id
        }
      })
    },
    createPartner () {
      this.showDialog({
        title: this._('Create partner'),
        fields: [
          { name: 'name', type: 'text', title: this._('Name') },
          { name: 'website', type: 'text', title: this._('Website'), placeholder: 'https://' },
          { name: 'contact_name', type: 'text', title: this._('Contact person'), half: 'left' },
          { name: 'contact_email', type: 'email', title: this._('Contact email'), half: 'right' },
          { name: 'tier', type: 'text', title: this._('Rate limit tier'), help: this._('Leave empty to use the default tier.') },
          { name: 'notes', type: 'textarea', title: this._('Notes'), rows: 3 }
        ],
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Create'),
            class: 'success',
            icon: 'plus',
            requiredFields: ['name'],
            handler: (data) => {
              this.savePartner(data)
            }
          }
        ]
      })
    },
    async savePartner (data) {
      const payload = {
        name: (data.name || '').trim(),
        website: (data.website || '').trim(),
        contact_name: (data.contact_name || '').trim(),
        contact_email: (data.contact_email || '').trim(),
        notes: data.notes || '',
        tier: (data.tier || '').trim()
      }
      try {
        const response = await axios.post(this.baseDomain + this.shopId + '/admin/partners.json?apikey=' + this.apiKey, payload)
        await this.loadPartners()
        if (response.data && response.data.id) {
          this.openPartner(response.data)
        }
      } catch (e) {
        this.showError(e, this._('Creating the partner failed.'), true)
      }
    }
  },
  mounted () {
    this.loadPartners()
    if (!this.events) {
      const events = {}
      events.partnerUpdate = debounce(() => this.loadPartners(), 200)
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.partner-table-wrap {
  margin-top: 10px;
  overflow-x: auto;
}
.partner-row {
  cursor: pointer;
}
.partner-review {
  margin-left: 4px;
}
.list-filter-col .checkbox {
  margin-top: 7px;
}
</style>
